import { Search } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CoverageLegend, CoverageMatrix } from "@/components/ci/coverage-matrix";
import {
  CellConfigSections,
  DetailRow,
  LabeledSelect,
  SearchableFilter,
  Section,
  shaOptions,
} from "@/components/ci/matrix-shared";
import { MatrixTabs } from "@/components/ci/matrix-tabs";
import { KpiTile } from "@/components/ci/overview-cells";
import { CiStatusBadge } from "@/components/ci/status-badge";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Skeleton } from "@/components/ui/skeleton";
import { useTimezone } from "@/contexts/timezone-context";
import {
  useCiTestHistory,
  useCryodBuildRevisions,
  useCryodCatalog,
  useCryodEngineVersions,
  useCryodMatrixStatus,
  useCryodVersions,
} from "@/hooks/use-ci";
import { usePageTitle } from "@/hooks/use-page-title";
import { useSearchParamSetter } from "@/hooks/use-search-param";
import type { CryodCatalogCell } from "@/lib/types";
import { cn, formatBytes } from "@/lib/utils";

const ALL = "all";
// URL value for an explicit "No tag (declared)" choice. An *absent* ?tag=
// param means "default to the latest tag", so explicit no-tag needs its own
// value or it would snap back to latest. Plain "none" (not "__none__") keeps
// the URL readable — cryod tags are version-shaped ("0.2.3-rc4"), so a
// literal "none" tag can't occur.
const NO_TAG_PARAM = "none";

// Display label for an engine value. dynamo is not a matrix engine today
// (it returns later as an orthogonal per-engine toggle) but is mapped for
// completeness so any stray value still renders readably.
const ENGINE_LABELS: Record<string, string> = {
  vllm: "vLLM",
  sglang: "SGLang",
  trtllm: "TRT-LLM",
  dynamo: "Dynamo",
};
function engineDisplayName(engine: string): string {
  return ENGINE_LABELS[engine] ?? engine;
}

export function CiMatrixPage() {
  usePageTitle("Coverage Matrix");
  const [searchParams, setSearchParams] = useSearchParams();
  const setParam = useSearchParamSetter();

  const quantization = searchParams.get("quant") ?? ALL;
  const search = searchParams.get("q") ?? "";
  const selectedName = searchParams.get("sel");

  const catalogQuery = useCryodCatalog();
  // ?mrev= pinned a Mooncake build; that sha is gone (INF-2041), so old bookmarks drop it.
  useEffect(() => {
    if (searchParams.has("mrev")) setParam("mrev", "", { replace: true });
  }, [searchParams, setParam]);
  const versionsQuery = useCryodVersions(searchParams.get("rev"));

  // The matrix shows the official cross-restore catalog only (the per-model
  // gpu-{vllm,sglang,trtllm}-cross-restore/ files); bench / flag-coverage /
  // failure / memory-check tests, the dynamo suites, and the flat
  // gpu-trtllm*.yaml suites (tp8/ep/pp) are excluded.
  const officialCells = useMemo(
    () => (catalogQuery.data ?? []).filter((cell) => cell.official),
    [catalogQuery.data],
  );

  const engineOptions = useMemo(() => {
    const engines = [...new Set(officialCells.map((cell) => cell.engine))];
    // vLLM first (the default engine), then the rest alphabetically.
    return engines.sort((left, right) =>
      left === "vllm" ? -1 : right === "vllm" ? 1 : left.localeCompare(right),
    );
  }, [officialCells]);
  // No "all engines" view — the matrix always shows exactly one engine. Default
  // to the first option (vLLM when present); coerce a stale/unknown ?engine=
  // (e.g. the old "all") back to the default once the catalog has loaded.
  const engineParam = searchParams.get("engine");
  const engine =
    engineParam && engineOptions.includes(engineParam) ? engineParam : (engineOptions[0] ?? "vllm");

  const tagParam = searchParams.get("tag");
  // /cryod-versions is ordered newest-run-first, so [0] is the latest tag.
  const latestVersion = versionsQuery.data?.[0]?.version ?? null;
  const tag = tagParam === NO_TAG_PARAM ? null : (tagParam ?? latestVersion);
  // Engine-version overlay filter. Stored as ?ev= — the control now holds vLLM
  // OR SGLang versions depending on the selected engine; ?vllm= is read as a
  // fallback for old bookmarks from when this was vLLM-only.
  const engineVersion = searchParams.get("ev") ?? searchParams.get("vllm");
  const revision = searchParams.get("rev");
  // Facet the sha list by the EXPLICITLY chosen tag only: the derived default
  // (latest of the sha-narrowed versions) would loop a sha selection back into
  // narrowing its own list.
  const chosenTag = tagParam === NO_TAG_PARAM ? null : tagParam;
  const buildRevisionsQuery = useCryodBuildRevisions(chosenTag);
  // Any one of the three filters turns on the run-status overlay.
  const statusFilterActive = tag != null || engineVersion != null || revision != null;
  // Hold the engine-versions fetch until BOTH the tag and the catalog are
  // settled. The tag (when absent) defaults to the latest version, and `engine`
  // defaults to vLLM until the catalog loads — so fetching early on a cold
  // ?engine=sglang load would grab the vLLM list, then refetch SGLang once the
  // catalog resolves (extra round-trip + a "no runs" flash). Passing `null`
  // engine until the catalog is known keeps the request scoped correctly.
  const engineVersionsQuery = useCryodEngineVersions(
    tag,
    catalogQuery.data ? engine : null,
    (tagParam != null || !versionsQuery.isPending) && !catalogQuery.isPending,
  );
  const matrixStatusQuery = useCryodMatrixStatus(tag, engineVersion, revision);
  const statusByTestName = useMemo(
    () => new Map((matrixStatusQuery.data ?? []).map((entry) => [entry.test_name, entry])),
    [matrixStatusQuery.data],
  );
  const tagOptions = useMemo(() => {
    const versions = (versionsQuery.data ?? []).map((option) => option.version);
    // A stale bookmark may carry a tag that aged out of the list — keep it
    // selectable so the trigger renders it and the user can clear it.
    if (tag && !versions.includes(tag)) versions.unshift(tag);
    return versions;
  }, [versionsQuery.data, tag]);
  const engineVersionOptions = useMemo(() => {
    const versions = (engineVersionsQuery.data ?? []).map((option) => option.engine_version);
    // Same stale-bookmark rule as tagOptions — keep an aged-out (or
    // tag-mismatched) deep-linked version selectable so it can be cleared.
    if (engineVersion && !versions.includes(engineVersion)) versions.unshift(engineVersion);
    return versions;
  }, [engineVersionsQuery.data, engineVersion]);
  const cryodShaOptions = useMemo(
    () => shaOptions(buildRevisionsQuery.data?.cryod, revision),
    [buildRevisionsQuery.data, revision],
  );
  const quantizationOptions = useMemo(
    () => [
      ALL,
      ...[
        ...new Set(officialCells.map((cell) => cell.quantization).filter((value) => value != null)),
      ]
        .sort()
        .map((value) => value as string),
    ],
    [officialCells],
  );

  const filteredCells = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return officialCells.filter((cell) => {
      if (cell.engine !== engine) return false;
      if (quantization !== ALL && (cell.quantization ?? "") !== quantization) return false;
      if (
        needle &&
        !`${cell.model_id} ${cell.config} ${cell.name}`.toLowerCase().includes(needle)
      ) {
        return false;
      }
      return true;
    });
  }, [officialCells, engine, quantization, search]);

  const summary = useMemo(() => {
    return {
      cells: filteredCells.length,
      models: new Set(filteredCells.map((cell) => cell.model_id)).size,
      configs: new Set(filteredCells.map((cell) => cell.config)).size,
      tiers: new Set(filteredCells.map((cell) => cell.gpu_type ?? "any")).size,
    };
  }, [filteredCells]);

  // Per-cell rollup for the selected filters. Latest run wins; skips count as
  // no-run; xfail is its own known-issue bucket, xpass counts as passing.
  const statusSummary = useMemo(() => {
    if (!statusFilterActive) return null;
    let passing = 0;
    let failing = 0;
    let knownIssue = 0;
    let noRun = 0;
    for (const cell of filteredCells) {
      const latestStatus = statusByTestName.get(cell.name)?.latest_status;
      if (latestStatus === "pass" || latestStatus === "xpass") passing += 1;
      else if (latestStatus === "fail" || latestStatus === "error") failing += 1;
      else if (latestStatus === "xfail") knownIssue += 1;
      else noRun += 1;
    }
    return { passing, failing, knownIssue, noRun };
  }, [statusFilterActive, filteredCells, statusByTestName]);

  const selectedCell = useMemo(
    () => officialCells.find((cell) => cell.name === selectedName) ?? null,
    [officialCells, selectedName],
  );

  // Tests at the clicked cell's coordinate (engine × model × config × tier) —
  // usually one; the drawer lists them when a cell holds more than one. Engine
  // is part of the coordinate: vLLM and SGLang can share a model/config/tier
  // (e.g. Kimi-K2.6 tp8-h200), so without it the SGLang cell would also list
  // the vLLM test at the same spot (and vice versa).
  const siblings = useMemo(() => {
    if (!selectedCell) return [];
    return officialCells.filter(
      (cell) =>
        cell.engine === selectedCell.engine &&
        cell.model_id === selectedCell.model_id &&
        cell.config === selectedCell.config &&
        (cell.gpu_type ?? "") === (selectedCell.gpu_type ?? ""),
    );
  }, [officialCells, selectedCell]);

  return (
    <div className="mx-auto max-w-screen-2xl space-y-4 p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Coverage Matrix</h1>
          <p className="text-sm text-muted-foreground">
            The official cryod cross-restore matrix — model × config × hardware, parsed from the
            per-model vLLM / SGLang / TRT-LLM test YAMLs. Pick an engine, then any of cryod tag,
            engine version, or the cryod build sha to overlay pass/fail status; click a tile for the
            full test config and its runs.
          </p>
        </div>
        <MatrixTabs current="/ci/matrix" />
      </div>

      {/* Control bar */}
      <div className="flex flex-wrap items-center gap-3">
        <LabeledSelect
          label="Engine"
          value={engine}
          options={engineOptions}
          onChange={(value) => {
            // The engine-version dropdown is engine-specific, so switching engines
            // resets it to All — one history entry for both params.
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.set("engine", value);
              next.delete("ev");
              next.delete("vllm");
              return next;
            });
          }}
          renderLabel={(value) => engineDisplayName(value)}
        />
        <LabeledSelect
          label="Quant"
          value={quantization}
          options={quantizationOptions}
          onChange={(value) => setParam("quant", value)}
          renderLabel={(value) => (value === ALL ? "All quant" : value)}
        />
        <SearchableFilter
          label="Tag"
          value={tag}
          options={tagOptions}
          noneLabel="No tag (declared)"
          noun="tag"
          onChange={(value) => {
            // The engine-version dropdown cascades from the tag; the sha
            // filter stands alone (a mismatch is just an empty overlay).
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              next.set("tag", value || NO_TAG_PARAM);
              next.delete("ev");
              next.delete("vllm");
              return next;
            });
          }}
        />
        <SearchableFilter
          label="Cryod sha"
          value={revision}
          options={cryodShaOptions}
          onChange={(value) => setParam("rev", value)}
        />
        <SearchableFilter
          label={engineDisplayName(engine)}
          value={engineVersion}
          options={engineVersionOptions}
          noneLabel="All versions"
          noun="version"
          onChange={(value) => {
            // Write the new ?ev= and drop any legacy ?vllm= in one history
            // entry, so an old bookmark doesn't linger as ?vllm=X&ev=Y.
            setSearchParams((previous) => {
              const next = new URLSearchParams(previous);
              if (value) next.set("ev", value);
              else next.delete("ev");
              next.delete("vllm");
              return next;
            });
          }}
        />
        <div className="relative ml-auto max-w-xs flex-1">
          <Search className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(event) => setParam("q", event.target.value, { replace: true })}
            placeholder="Filter model or config…"
            className="h-8 pl-8"
          />
        </div>
      </div>

      {/* Pinned-build notice: explains why the overlay may go sparse/all-no-run
          (only this build's runs count; older runs predate commit capture). */}
      {revision && (
        <p className="text-xs text-muted-foreground">
          Overlay pinned to build{" "}
          <span className="font-mono text-foreground">cryod@{revision.slice(0, 7)}</span> — tiles
          reflect only runs from this build; runs from before build-commit capture won't appear.
        </p>
      )}

      {/* Summary + legend */}
      <div
        className={cn(
          "grid grid-cols-2 gap-3",
          statusSummary ? "sm:grid-cols-7" : "sm:grid-cols-4",
        )}
      >
        <KpiTile label="Declared cells" value={summary.cells} />
        <KpiTile label="Models" value={summary.models} />
        <KpiTile label="Configs" value={summary.configs} />
        <KpiTile label="Hardware tiers" value={summary.tiers} />
        {statusSummary && (
          <>
            <KpiTile label="Passing" value={statusSummary.passing} />
            <KpiTile label="Failing" value={statusSummary.failing} />
            <KpiTile label="Known issue" value={statusSummary.knownIssue} />
            <KpiTile label="No runs" value={statusSummary.noRun} />
          </>
        )}
      </div>
      <CoverageLegend statusMode={statusFilterActive} />

      {catalogQuery.isLoading ? (
        <div className="space-y-2">
          {["a", "b", "c", "d", "e", "f"].map((key) => (
            <Skeleton key={key} className="h-8 w-full" />
          ))}
        </div>
      ) : (
        <CoverageMatrix
          cells={filteredCells}
          selectedName={selectedName}
          onSelect={(cell) => setParam("sel", cell.name)}
          statusByTestName={statusFilterActive ? statusByTestName : null}
        />
      )}

      <Sheet
        open={selectedCell != null}
        onOpenChange={(open) => {
          if (!open) setParam("sel", "");
        }}
      >
        <SheetContent side="right" className="w-full gap-0 p-0 sm:max-w-2xl">
          {selectedCell && (
            <CellDetail
              cell={selectedCell}
              siblings={siblings}
              tag={tag}
              engineVersion={engineVersion}
              revision={revision}
              onSelectSibling={(name) => setParam("sel", name)}
            />
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function CellDetail({
  cell,
  siblings,
  tag,
  engineVersion,
  revision,
  onSelectSibling,
}: {
  cell: CryodCatalogCell;
  siblings: CryodCatalogCell[];
  tag: string | null;
  engineVersion: string | null;
  revision: string | null;
  onSelectSibling: (name: string) => void;
}) {
  return (
    <>
      <SheetHeader className="border-b border-border">
        <SheetTitle className="font-mono text-sm">{cell.model_id}</SheetTitle>
        <div className="flex flex-wrap items-center gap-1.5 pt-1">
          <Badge variant="secondary" className="font-mono text-[10px]">
            {cell.config}
          </Badge>
          <Badge variant="secondary" className="font-mono text-[10px]">
            {cell.gpu_type ?? "any tier"}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {cell.engine}
          </Badge>
          <Badge variant="outline" className="text-[10px]">
            {cell.operation}
          </Badge>
          {cell.min_vllm_version && (
            <Badge variant="outline" className="text-[10px]">
              {engineDisplayName(cell.engine)} ≥ {cell.min_vllm_version}
            </Badge>
          )}
        </div>
      </SheetHeader>

      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        {siblings.length > 1 && (
          <div className="space-y-1.5 rounded-md border border-border bg-muted/30 p-2">
            <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
              {siblings.length} tests at this cell — pick one
            </div>
            <div className="flex flex-col gap-1">
              {siblings.map((sibling) => (
                <button
                  key={sibling.name}
                  type="button"
                  onClick={() => onSelectSibling(sibling.name)}
                  className={cn(
                    "rounded px-2 py-1 text-left font-mono text-[11px] ring-1 transition-colors",
                    sibling.name === cell.name
                      ? "bg-primary/15 text-foreground ring-primary/40"
                      : "text-muted-foreground ring-border hover:bg-muted/50 hover:text-foreground",
                  )}
                >
                  {sibling.name}
                </button>
              ))}
            </div>
          </div>
        )}
        {(tag || engineVersion) && (
          <CellRunsSection
            testName={cell.name}
            engine={cell.engine}
            tag={tag}
            engineVersion={engineVersion}
            revision={revision}
          />
        )}
        <Section title="Coordinate">
          <DetailRow
            label="Test name"
            value={<span className="font-mono text-xs">{cell.name}</span>}
          />
          <DetailRow
            label="Source"
            value={<span className="font-mono text-xs">{cell.source_file}</span>}
          />
          <DetailRow
            label="Parallelism"
            value={
              <span className="font-mono text-xs">
                {[
                  cell.tensor_parallel != null ? `TP=${cell.tensor_parallel}` : null,
                  cell.data_parallel != null ? `DP=${cell.data_parallel}` : null,
                  cell.expert_parallel ? "EP" : null,
                ]
                  .filter(Boolean)
                  .join(" · ") || "—"}
              </span>
            }
          />
          <DetailRow label="Quantization" value={cell.quantization ?? "—"} />
          {cell.tags.length > 0 && (
            <DetailRow
              label="Tags"
              value={
                <div className="flex flex-wrap gap-1">
                  {cell.tags.map((tag) => (
                    <Badge key={tag} variant="secondary" className="text-[10px]">
                      {tag}
                    </Badge>
                  ))}
                </div>
              }
            />
          )}
        </Section>

        <CellConfigSections config={cell.full_config} />
      </div>
    </>
  );
}

// Runs of this test under the selected cryod tag and/or vLLM version, newest
// first; each row links to the full test-result detail page. The tag is a bare
// image tag — runs of both transports (+rdma / +tcp) fold in, so each row
// badges its protocol. The vLLM version is an exact image tag (CUDA variants
// are distinct), matching the matrix overlay's filtering.
function CellRunsSection({
  testName,
  engine,
  tag,
  engineVersion,
  revision,
}: {
  testName: string;
  engine: string;
  tag: string | null;
  engineVersion: string | null;
  revision: string | null;
}) {
  const historyParams: Record<string, string> = { test_type: "cryod", limit: "20" };
  if (tag) historyParams.version_base = tag;
  if (engineVersion) historyParams.engine_version = engineVersion;
  if (revision) historyParams.revision = revision;
  const historyQuery = useCiTestHistory(testName, historyParams);
  const { formatTimestamp } = useTimezone();
  const entries = historyQuery.data ?? [];

  // Cell-level checkpoint summary: the most recent run (entries are started_at
  // DESC) that actually recorded a size. weights === 0 is a non-arena dump where
  // the weights/state split isn't separable, so the split rows are hidden — same
  // convention as the test-detail Checkpoint card.
  const checkpointEntry = entries.find((entry) => entry.checkpoint_size_bytes != null);
  const checkpointSize = checkpointEntry?.checkpoint_size_bytes ?? null;
  const checkpointWeights = checkpointEntry?.checkpoint_weights_bytes ?? null;

  return (
    <>
      <Section
        title={`Runs · ${[tag, engineVersion, revision ? `@${revision.slice(0, 7)}` : null]
          .filter(Boolean)
          .join(" · ")}`}
      >
        {historyQuery.isLoading ? (
          <Skeleton className="h-8 w-full" />
        ) : entries.length === 0 ? (
          <div className="text-xs text-muted-foreground">
            {revision
              ? `No runs match commit @${revision.slice(0, 7)} — runs from before build-commit capture can't be pinned.`
              : "No runs for this test under the selected filters."}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {entries.map((entry) => {
              const protocolStart = entry.version.indexOf("+");
              const protocol = protocolStart === -1 ? null : entry.version.slice(protocolStart + 1);
              return (
                <Link
                  key={entry.result_id}
                  to={`/ci/${encodeURIComponent(entry.run_id)}/tests/${entry.result_id}`}
                  className="flex items-center gap-2 rounded px-2 py-1 ring-1 ring-border transition-colors hover:bg-muted/50"
                >
                  <CiStatusBadge status={entry.status} />
                  {protocol && (
                    <Badge variant="outline" className="font-mono text-[10px]">
                      {protocol}
                    </Badge>
                  )}
                  {entry.engine_version && (
                    <Badge
                      variant="outline"
                      className="font-mono text-[10px]"
                      title="Engine version (workload image tag)"
                    >
                      {engineDisplayName(engine)} {entry.engine_version}
                    </Badge>
                  )}
                  {entry.gpu_type && (
                    <Badge variant="outline" className="font-mono text-[10px]" title="GPU type">
                      {entry.gpu_type}
                    </Badge>
                  )}
                  {entry.nvidia_driver_version && (
                    <Badge
                      variant="outline"
                      className="font-mono text-[10px]"
                      title="NVIDIA driver version"
                    >
                      {entry.nvidia_driver_version}
                    </Badge>
                  )}
                  <span className="font-mono text-[11px] text-muted-foreground">
                    {entry.started_at ? formatTimestamp(entry.started_at) : "—"}
                  </span>
                  <div className="ml-auto flex items-center gap-2">
                    {entry.checkpoint_size_bytes != null && (
                      <span
                        className="font-mono text-[11px] text-muted-foreground"
                        title="Checkpoint size"
                      >
                        {formatBytes(entry.checkpoint_size_bytes)}
                      </span>
                    )}
                    {entry.total_restore_ms != null && (
                      <span className="text-[11px] text-muted-foreground">
                        {`${(entry.total_restore_ms / 1000).toFixed(1)}s restore`}
                      </span>
                    )}
                    {entry.machine_name && (
                      <span className="max-w-[10rem] truncate font-mono text-[11px] text-muted-foreground">
                        {entry.machine_name}
                      </span>
                    )}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </Section>
      {checkpointEntry && checkpointSize != null && (
        <Section title="Checkpoint">
          <DetailRow label="Size" value={formatBytes(checkpointSize)} />
          {checkpointWeights != null && checkpointWeights > 0 && (
            <>
              <DetailRow label="Weights" value={formatBytes(checkpointWeights)} />
              <DetailRow
                label="Excl. weights"
                value={formatBytes(checkpointSize - checkpointWeights)}
              />
            </>
          )}
          <DetailRow
            label="From run"
            value={checkpointEntry.started_at ? formatTimestamp(checkpointEntry.started_at) : "—"}
          />
        </Section>
      )}
    </>
  );
}
